import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { IconLayer, PathLayer } from '@deck.gl/layers';
import * as _deckLayers from '@deck.gl/layers';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TextLayer = (_deckLayers as any).TextLayer;
import type { AircraftState, IcaoCategory } from '../../../types/mapEngine';
import { getIcaoCategory, WINGSPAN_M } from './aircraftTypes';
import { AIRCRAFT_SILHOUETTES, svgToDataUrl } from './silhouettes/index';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  map: mapboxgl.Map;
  aircraft: AircraftState[];
  visible: boolean;
  showTrails?: boolean;
  onAircraftClick?: (ac: AircraftState | null) => void;
}

// ---------------------------------------------------------------------------
// Status → RGB color mapping for Deck.gl
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, [number, number, number]> = {
  parked:    [160, 160, 160],
  pushback:  [255, 152,   0],
  taxiing:   [255, 215,   0],
  holding:   [ 38, 198, 218],
  emergency: [244,  67,  54],
};

// ---------------------------------------------------------------------------
// Pre-compute one data URL per ICAO category (computed once at module load)
// ---------------------------------------------------------------------------

const ICON_MAP = (Object.keys(AIRCRAFT_SILHOUETTES) as IcaoCategory[]).reduce(
  (acc, cat) => ({ ...acc, [cat]: svgToDataUrl(AIRCRAFT_SILHOUETTES[cat]) }),
  {} as Record<IcaoCategory, string>,
);

// ---------------------------------------------------------------------------
// Trail configuration
// ---------------------------------------------------------------------------

const TRAIL_MAX_POSITIONS = 45; // animation frames retained per aircraft

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AircraftDeckLayer: React.FC<Props> = ({
  map,
  aircraft,
  visible,
  showTrails = true,
  onAircraftClick,
}) => {
  const overlayRef      = useRef<MapboxOverlay | null>(null);
  const trailRef        = useRef<Map<string, [number, number][]>>(new Map());
  const clickHandlerRef = useRef(onAircraftClick);
  clickHandlerRef.current = onAircraftClick;

  // Mount: create MapboxOverlay and register it as a Mapbox IControl
  useEffect(() => {
    const overlay = new MapboxOverlay({
      interleaved: false,
      layers: [],
      onClick: (info: { object?: AircraftState }) => {
        clickHandlerRef.current?.(info.object ?? null);
      },
    });
    map.addControl(overlay as unknown as mapboxgl.IControl);
    overlayRef.current = overlay;
    return () => {
      map.removeControl(overlay as unknown as mapboxgl.IControl);
      overlayRef.current = null;
    };
  }, [map]);

  // Update layers whenever aircraft list or visibility changes
  useEffect(() => {
    if (!overlayRef.current) return;

    // ── Maintain trail history ────────────────────────────────────────────
    const history = trailRef.current;
    const activeIds = new Set(aircraft.map((a) => a.id));

    for (const id of history.keys()) {
      if (!activeIds.has(id)) history.delete(id);
    }
    for (const ac of aircraft) {
      if (!history.has(ac.id)) history.set(ac.id, []);
      const trail = history.get(ac.id)!;
      const last = trail[trail.length - 1];
      if (!last || last[0] !== ac.position[0] || last[1] !== ac.position[1]) {
        trail.push([...ac.position] as [number, number]);
        if (trail.length > TRAIL_MAX_POSITIONS) trail.shift();
      }
    }

    if (!visible || aircraft.length === 0) {
      overlayRef.current.setProps({ layers: [] });
      return;
    }

    // ── Trail layer ───────────────────────────────────────────────────────
    type TrailEntry = { id: string; path: [number, number][]; color: [number, number, number] };
    const trailData: TrailEntry[] = [];
    if (showTrails) {
      for (const ac of aircraft) {
        const trail = history.get(ac.id);
        if (trail && trail.length > 1) {
          trailData.push({
            id: ac.id,
            path: trail,
            color: STATUS_COLORS[ac.status] ?? STATUS_COLORS.parked,
          });
        }
      }
    }

    const trailLayer = trailData.length > 0
      ? new PathLayer<TrailEntry>({
          id: 'me-aircraft-trails',
          data: trailData,
          getPath: (d: TrailEntry) => d.path,
          getColor: (d: TrailEntry) => [...d.color, 110] as [number, number, number, number],
          getWidth: 2,
          widthUnits: 'pixels',
          widthMinPixels: 1,
          widthMaxPixels: 3,
        })
      : null;

    // ── Icon layer ────────────────────────────────────────────────────────
    const iconLayer = new IconLayer<AircraftState>({
      id: 'me-aircraft-icons',
      data: aircraft,
      getPosition: (d: AircraftState) => d.position,
      getIcon: (d: AircraftState) => {
        const cat = getIcaoCategory(d.icaoType);
        return { url: ICON_MAP[cat], width: 100, height: 100, mask: true };
      },
      getColor: (d: AircraftState) => STATUS_COLORS[d.status] ?? STATUS_COLORS.parked,
      getAngle: (d: AircraftState) => -d.heading,
      sizeUnits: 'meters',
      sizeScale: 1,
      getSize: (d: AircraftState) => WINGSPAN_M[getIcaoCategory(d.icaoType)],
      sizeMinPixels: 12,
      sizeMaxPixels: 120,
      pickable: true,
      billboard: false,
    });

    // ── Text layer ────────────────────────────────────────────────────────
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const TextLayerAny = TextLayer as any;
    const textLayer = new TextLayerAny({
      id: 'me-aircraft-labels',
      data: aircraft,
      getPosition: (d: AircraftState) => d.position,
      getText: (d: AircraftState) => `${d.callsign}\n${d.icaoType}`,
      getSize: 11,
      getColor: [255, 255, 255],
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'top',
      background: true,
      getBackgroundColor: [0, 0, 0, 160],
      backgroundPadding: [3, 1, 3, 1],
      fontFamily: '"DIN Pro Medium", Arial, sans-serif',
      billboard: true,
      sizeUnits: 'pixels',
    });

    overlayRef.current.setProps({
      layers: [trailLayer, iconLayer, textLayer].filter(Boolean),
    });
    // overlayRef.current is stable; included to satisfy exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft, visible, showTrails, overlayRef.current]);

  return null;
};

export default AircraftDeckLayer;
